import React from "react";
import {
  Button,
  Typography,
  Space,
  Drawer,
  Tag,
  Spin,
  message,
} from "antd";
import {
  CloseOutlined,
  PrinterOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import HCPrint from "../../../../lib/printer/hardWareControllerPrinter";
import CWHCPrint from "../../../../lib/printer/CWHardwareController";
import { xmlPrint } from "../../../../lib/printer/xmlPrinter";
import { htmlPrint } from "../../../../lib/printer/htmlPrinter";
import { getWorkflowData } from "../../../../utils/getWorkflowData";
import { reconcileDuplicateOrderLines } from "../../../../utils/reconcileDuplicateOrderLines";
import useAuthStore from "../../../../stores/useAuthStore";

const { Title } = Typography;

const SalesHistoryDrawer = ({
  salesHistoryDrawer,
  setSalesHistoryDrawer,
  selectedOrder,
  setSelectedOrder,
  loading,
  setLoading,
  currenciesList,
  posConfig,
  searchReturnHistory,
  setReturnItem,
  setReturnQty,
  setDataSource,
  setSelectedReturnOrder,
  setCheckedKeys,
  setReturnDrawerOpen,
  ObFlag,
  CWHardwareController,
  isPrintModeXML,
  upsertPOSLog,
  dispatchEvent,
}) => {
  // BUG-2111: htmlPrint()'s window.print() dialog isn't usable inside the CW Browser/Electron
  // shell — on a till with none of ObFlag/CWHardwareController/isPrintModeXML explicitly set
  // (a valid config for tills relying on the embedded hardware-controller bridge), this used to
  // fall straight through to htmlPrint() and silently fail to print. Route through the same
  // native bridge printController.js already uses for this case (see its F-04 comment) instead.
  const printReprintOrder = (reprintOrder) => {
    const printerConfigured = !!useAuthStore.getState().tillData?.tillAccess?.cwrTill?.hardwareController?.imageUrl;
    if (ObFlag) {
      HCPrint(reprintOrder, "Duplicate Copy");
    } else if (CWHardwareController) {
      CWHCPrint(reprintOrder, "Duplicate Copy");
    } else if (isPrintModeXML) {
      xmlPrint(reprintOrder, "Duplicate Copy");
    } else if (window.electronAPI && printerConfigured) {
      CWHCPrint(reprintOrder, "Duplicate Copy");
    } else {
      htmlPrint(reprintOrder, "Duplicate Copy");
    }
  };

  return (
    <Drawer
      title={null}
      placement="right"
      onClose={() => {
        setSalesHistoryDrawer(false);
      }}
      rootClassName="sales-history-drawer"
      styles={{ header: { background: "white", borderBottom: "none" } }}
      styles={{ body: { padding: "1.5rem", background: "#f3f4f6" } }}
      open={salesHistoryDrawer}
      width="62%"
      closable={false}
      styles={{ mask: { backgroundColor: "rgba(0, 0, 0, 0.3)" } }}
    >
      {selectedOrder && (
        <>
          {loading && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(255,255,255,0.5)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 9999,
              }}
            >
              <Spin size="large" />
            </div>
          )}

          <div className="sales-history-drawer-body" style={{ height: "100%", display: "flex", flexDirection: "column" }}>
            {/* HEADER */}
            <div
              className="sales-history-header"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "10px",
              }}
            >
              <Title level={4} className="sales-history-title" style={{ margin: 0, fontSize: "22px", fontWeight: 600 }}>
                Order {selectedOrder.documentno}
              </Title>

              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={() => {
                  setSelectedOrder(null);
                  setSalesHistoryDrawer(false);
                }}
                style={{ border: "none", boxShadow: "none" }}
              />
            </div>

            {/* CUSTOMER + TIME */}
            <div style={{ marginBottom: "18px", color: "#666" }}>
              {selectedOrder.customer?.name || "Walk In Customer"} • {selectedOrder.orderTime}
            </div>

            {/* ORDER TOTAL CARD */}
            <div
              className="sales-history-total-card"
              style={{
                border: "1px solid #e6e6e6",
                borderRadius: "10px",
                padding: "10px",
                marginBottom: "10px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <div style={{ fontSize: "13px", color: "#777" }}>Order Total</div>
                <div style={{ fontSize: "18px", fontWeight: 600 }}>
                  {currenciesList[0].symbolRightSide === "Y" ? `${currenciesList[0].currSymbol} ` : ""}
                  {selectedOrder.total}
                  {currenciesList[0].symbolRightSide === "N" ? ` ${currenciesList[0].currSymbol}` : ""}
                </div>
              </div>

              {/* SALE TYPE BADGE */}
              <Tag
                style={{
                  background: "#f3f4f6",
                  color: "#2f3856",
                  border: "1px solid #e6e6e6",
                  borderRadius: "12px",
                  padding: "4px 12px",
                  fontSize: "16px",
                  fontWeight: "500",
                }}
              >
                {selectedOrder.saleType?.name}
              </Tag>
            </div>

            {/* ITEMS TITLE */}
            <Title level={5} style={{ marginBottom: "12px" }}>
              Ordered Items
            </Title>

            {/* ITEMS LIST */}
            <div style={{ flex: 1, overflow: "auto", paddingRight: "4px" }}>
              <Space direction="vertical" style={{ width: "100%" }} size={12}>
                {reconcileDuplicateOrderLines(selectedOrder.items, selectedOrder.totalQty)?.map((item, index) => {
                  const alreadyReturned = item.returnQty >= item.qty;
                  return (
                    <div
                      key={index}
                      className="sales-history-item-row"
                      style={{
                        border: "1px solid #e6e6e6",
                        borderRadius: "10px",
                        padding: "14px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      {/* LEFT */}
                      <div>
                        <div style={{ fontSize: "15px", fontWeight: 600 }}>{item?.productAliasName ? item?.productAliasName : item.name}</div>
                        <div style={{ fontSize: "13px", color: "#777", marginTop: "4px" }}>
                          Qty: {item.qty} × {item.salePrice}
                        </div>
                      </div>

                      {/* RIGHT */}
                      <div className="sales-history-item-right" style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <div style={{ fontSize: "15px", fontWeight: 600 }}>
                          {currenciesList[0].symbolRightSide === "Y" ? `${currenciesList[0].currSymbol} ` : ""}
                          {item.salePrice}
                          {currenciesList[0].symbolRightSide === "N" ? ` ${currenciesList[0].currSymbol}` : ""}
                        </div>

                        {parseFloat(selectedOrder?.total) > 0 && posConfig?.showSalesReturn === "Y" && selectedOrder?.layAway === "N" && (
                          <Button
                            disabled={alreadyReturned}
                            icon={<UndoOutlined />}
                            style={{
                              borderRadius: "6px",
                              border: "1px solid #d9d9d9",
                              background: alreadyReturned ? "#ccc" : "#2f3856",
                              color: "#fff",
                              height: "32px",
                              padding: "0 14px",
                              display: "flex",
                              alignItems: "center",
                              gap: "2px",
                            }}
                            onClick={async () => {
                              setLoading(true);

                              const orderData = await searchReturnHistory(selectedOrder.documentno, selectedOrder);

                              setLoading(false);
                              if (!orderData) return;

                              const line = orderData.line.find((l) => l.sOrderlineID === item.sOrderlineID);

                              if (!line) return;

                              const remainingQty = line.returnQty > 0 ? line.qty - line.returnQty : line.qty;

                              if (remainingQty <= 0) {
                                message.warning("Already fully returned");
                                return;
                              }

                              setReturnItem({
                                ...line,
                                name: line.product?.name,
                                salePrice: line.grossstd,
                                remainingQty,
                              });
                              setReturnQty(remainingQty);

                              const row = {
                                ...line,
                                key: "0",
                                qtyToReturn: remainingQty,
                                remainingQty,
                              };

                              setDataSource([row]);
                              setSelectedReturnOrder({
                                line: [row],
                                cwrB2cCustomer: orderData.cwrB2cCustomer,
                                documentno: orderData.documentno,
                              });

                              setCheckedKeys([row.key]);
                              setReturnDrawerOpen(true);
                            }}
                          >
                            {alreadyReturned ? "Returned" : "Return"}
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </Space>
            </div>

            {/* FOOTER */}
            <div
              className="sales-history-footer"
              style={{
                padding: "16px 10px",
                borderTop: "1px solid #f0f0f0",
                display: "flex",
                gap: "12px",
                marginTop: "12px",
              }}
            >
              <Button
                icon={<PrinterOutlined />}
                style={{
                  flex: 1,
                  height: "40px",
                  borderRadius: "6px",
                }}
                onClick={async () => {
                  // BUG-1920: reconcile duplicate-inserted lines (see reconcileDuplicateOrderLines.js)
                  // for print only — selectedOrder.items keeps its original per-line sOrderlineID
                  // identity for the Return button's lookup above. itemsSummery/groupedItemsByTaxRate
                  // are separately pre-computed in useOrderHistory.js from the *raw* (unreconciled)
                  // line array, so they must be reset here too — CWHardwareController.js recomputes
                  // both from receiptData.items whenever they arrive empty (the same contract the
                  // mobile app's print payload relies on), otherwise "Total Qty" on the printed
                  // receipt stays doubled even though the item rows are fixed.
                  const reprintOrder = {
                    ...selectedOrder,
                    items: reconcileDuplicateOrderLines(selectedOrder.items, selectedOrder.totalQty),
                    itemsSummery: {},
                    groupedItemsByTaxRate: [],
                    isReprint: true,
                  };

                  // BUG-2229: a reprint is gated ONLY by the "Receipt Reprint Request" rule.
                  // This previously keyed on `workflowRules.length > 0` with the direct-print
                  // fallback in the else, so any till that had other workflow rules (Sales Return
                  // Approval, Cancel KOT, …) but no reprint rule dropped into the forEach, matched
                  // nothing, and printed nothing. Match the specific rule; when it is absent, print
                  // directly exactly as a till with no rules at all already did.
                  const { workflowRules, approvers } = await getWorkflowData();
                  const reprintRule = (workflowRules || []).find((res) => res.eventName === "Receipt Reprint Request");

                  if (reprintRule) {
                    dispatchEvent(reprintRule.eventName, reprintRule.ruleName, reprintRule.ruleCondition, approvers, null, (eventData) => {
                      if (eventData?.approvalGranted) printReprintOrder(reprintOrder);
                    });
                  } else {
                    printReprintOrder(reprintOrder);
                    upsertPOSLog(selectedOrder, "RRC");
                  }
                }}
              >
                Reprint
              </Button>
            </div>
          </div>
        </>
      )}
    </Drawer>
  );
};

export default React.memo(SalesHistoryDrawer);
